import { bookActions } from '@/actions';
import { turnstileActions } from '@/actions/turnstile';
import { Button } from '@/components/elements/forms/buttons';
import { ButtonProps } from '@/components/elements/forms/buttons/Button/Button';
import { usePinInput } from '@/components/elements/forms/PinInput/PinInput';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { ThreeDSComponent } from '@/components/modules/adyen';
import CardWrapper from '@/components/modules/CardWrapper';
import AdditionalParticipants from '@/components/modules/checkout/AdditionalParticipants/AdditionalParticipants';
import AdditionalParticipantsModal from '@/components/modules/checkout/AdditionalParticipantsModal/AdditionalParticipantsModal';
import BookingTerms from '@/components/modules/checkout/BookingTerms/BookingTerms';
import CheckoutDetails, { CheckoutDetailsProps } from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import CheckoutFormFields from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields';
import {
  CheckoutFormProvider,
  useCheckoutForm,
} from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields.hooks';
import CheckoutModal from '@/components/modules/checkout/CheckoutModal/CheckoutModal';
import {
  CheckoutModalProvider,
  useCheckoutModal,
} from '@/components/modules/checkout/CheckoutModal/CheckoutModal.hooks';
import CheckoutTerms from '@/components/modules/checkout/CheckoutTerms/CheckoutTerms';
import KlarnaOptionsModal from '@/components/modules/checkout/KlarnaOptionsModal';
import PromotedPaymentMethods from '@/components/modules/checkout/PromotedPaymentMethods/PromotedPaymentMethods';
import WalleyPhoneModal from '@/components/modules/checkout/WalleyPhoneModal';
import CreditCardModal from '@/components/modules/modals/CreditCardModal';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import SwishModal from '@/components/modules/modals/SwishModal';
import GoBack from '@/components/modules/pages/bokningar/GoBack';
import FloatingCTAWrapper from '@/components/modules/pages/booking/checkout/FloatingCTAWrapper';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import UpdateUserInfo from '@/components/modules/UpdateUserInfo/UpdateUserInfo';
import useUpdateUserInfo from '@/components/modules/UpdateUserInfo/UpdateUserInfo.hooks';
import ProtectedUserLogin from '@/components/templates/checkout/ProtectedUserLogin/ProtectedUserLogin';
import UserInfo from '@/components/templates/checkout/UserInfo/UserInfo';
import UserLogin from '@/components/templates/checkout/UserLogin/UserLogin';
import VerifyOTP from '@/components/templates/login/VerifyOTP';
import { config } from '@/config';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';

import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import WidgetPaymentModal from '@/features/booking-widget/components/WidgetPaymentModal/WidgetPaymentModal';
import { AdyenCheckoutComponent, useOnCompleteAdyenCheckout } from '@/features/payments';
import {
  usePaymentMethodsContext,
  UsePaymentMethodsProvider,
  WithPaymentMethodsLoaded,
} from '@/features/payments/hooks/usePaymentMethods';
import { KlarnaVariant } from '@/features/payments/types/adyen.types';
import { getValidKlarnaBrands } from '@/features/payments/utils/paymentConfig.utils';
import {
  classnames,
  getUserInitials,
  isSistaminuten,
  salonTracking,
  shouldRequireLogin,
  trackPage,
  trackPageCio,
  truncateString,
  url,
} from '@/helpers';
import { getScreenNameFromPath, trackMpEvent } from '@/helpers/analyticsTS';
import {
  buildBookingTrackingProps,
  getBookingCheckoutEmployee,
  getBookingCheckoutSummary,
  getBookingSubmitLabel,
  getCheckoutID,
  getCheckoutSubmitIconVariantIfAny,
  getCheckoutTerms,
  getCheckoutTermsVariant,
  getGooglePaySummaryFeeItems,
  getGooglePaySummaryGiftcardItems,
  getGooglePaySummaryProductItems,
  getGooglePaySummaryServiceItems,
  getPaymentRedirectResultIfAny,
  getUpdateEditProfileFields,
  guestCheckoutManager,
  hasPaymentMethod,
  isFullyPaidWithGiftcards,
  isStandardFormField,
  mapBookingCheckoutToCheckoutDetailsProps,
  mapBookStateToCheckoutDetailsProducts,
  mapBookStateToCheckoutDetailsServices,
} from '@/helpers/checkout';
import { bookingFlowTrackingSession, getSummaryProperties } from '@/helpers/checkoutFlowTrackingSession';
import { getContainmentMode, getContainmentPolicy } from '@/helpers/containmentPolicy';
import { isEmbedMode } from '@/helpers/embed';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { isBokadirektPayCheckout } from '@/helpers/placeHelperTS';
import { loggedUser, validatePhoneNumber } from '@/helpers/userHelper';
import withPlaceDetailsFallback from '@/hoc/withPlaceDetailsFallback';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useCardsManager, UseCardsProvider } from '@/hooks/adyen/useCards';
import { useCardPaymentTracking } from '@/hooks/checkout/useCardPaymentTracking';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { CheckoutFormDataProvider, useCheckoutFormData } from '@/hooks/useCheckoutFormData';
import useExternalScript from '@/hooks/useExternalScript';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import { usePaymentsHistory } from '@/hooks/usePaymentsHistory';
import { useWidgetLogin } from '@/hooks/useWidgetLogin';
import { useWidgetPayment } from '@/hooks/useWidgetPayment';
import { getLang, _s } from '@/locale';
import { _t } from '@/locale/i18n';
import { CheckoutMissingAction, SelectedPaymentMethod } from '@/types/checkout';
import { PaymentCard } from '@/types/paymentcards';
import { BookState, bookStateSchema } from '@/types/state/book';
import { User } from '@/types/user';
import * as Sentry from '@sentry/react';
import { MutableRefObject, useContext, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormHandleSubmit } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Turnstile from 'react-turnstile';
import { Dispatch } from 'redux';
import isEmail from 'validator/lib/isEmail';
import {
  BookingCheckoutProvider,
  bookingCheckoutStateSchema,
  isResolvingContainedSession,
  isWidgetPaymentBlockingSubmit,
  listenForBackNavigation,
  saveBookingCheckoutStateToLocalStorage,
  showsWidgetPaymentModal,
  useBookingCheckout,
  useWidgetPaymentReactions,
} from './BookingCheckout.hooks';

export const baseTranslationKey = 'pages.booking.checkout.BookingCheckout';

function getGuestCheckoutProps(place: BookState['place'], user: User, guestId: string) {
  const requiresLogin = !user && shouldRequireLogin(place);
  const shouldPromptGuestCheckout = !requiresLogin && Boolean(!isSistaminuten() && !user && !guestId);
  const isGuestCheckout = Boolean(!isSistaminuten() && !user && guestId);
  const loggedOutCheckout = Boolean(isSistaminuten() || isGuestCheckout);

  return { shouldPromptGuestCheckout, isGuestCheckout, loggedOutCheckout };
}

function handleUpdateUserInfoCallback({ success, retry, initiatedFrom }, closeUpdateUserInfo: () => void) {
  trackMpEvent({
    name: 'edit_profile_flow',
    properties: {
      step: success ? 'success' : 'error',
      initiated_from: initiatedFrom,
      booking_session_id: bookingFlowTrackingSession.getId(),
      error_type: success ? undefined : 'backend_error',
    },
  });

  if (success) closeUpdateUserInfo();

  toast(
    ({ closeToast }) => (
      <Snackbar
        label={_s(`${baseTranslationKey}.editProfileModal.snackbar.label.${success ? 'success' : 'error'}`)}
        type={success ? 'success' : 'danger'}
        action={
          <button onClick={success ? closeToast : retry}>
            {_s(`${baseTranslationKey}.editProfileModal.snackbar.action.${success ? 'success' : 'error'}`)}
          </button>
        }
      />
    ),
    { autoClose: 2000 },
  );
}

function onSubmitBookingValidation(
  {
    isTermsAccepted,
    isValidPaymentMethod,
    onCheckoutMissingAction,
  }: {
    isTermsAccepted: boolean;
    isValidPaymentMethod: boolean;
    onCheckoutMissingAction: (action: CheckoutMissingAction) => void;
  },
  handleFormSubmit: UseFormHandleSubmit<FieldValues>,
  callback: (missingActions: CheckoutMissingAction[]) => void,
) {
  const missingActions: CheckoutMissingAction[] = [];

  if (!isTermsAccepted) missingActions.push('practitioner-terms');
  if (!isValidPaymentMethod) missingActions.push('payment-method');

  missingActions.forEach((action) => onCheckoutMissingAction(action));

  handleFormSubmit(
    () => {
      callback(missingActions);
    },
    (errors) => {
      const fieldNames = Array.from(
        new Set(Object.keys(errors).map((formField) => (isStandardFormField(formField) ? formField : 'custom_field'))),
      );

      if (missingActions.length) fieldNames.push(...missingActions);

      bookingFlowTrackingSession.validationError(
        (() => {
          let errors = [];
          if (missingActions.includes('participants')) errors.push('missing_participants');
          if (missingActions.includes('practitioner-terms')) errors.push('merchant_terms');
          if (missingActions.includes('payment-method')) errors.push('payment_methods');
          if (missingActions.includes('email')) errors.push('email_missing');
          if (fieldNames.length) errors.push('form_fields');
          return errors;
        })(),
      );
    },
  )();
}

function validateAppliedBookingBundleIfAny(booking: BookState, dispatch: Dispatch): void {
  const { appliedBundle, services } = booking;

  if (!appliedBundle) return;

  const appliedBundleApplicableForCheckout = services.some(
    (service) => service.id === appliedBundle?.service?.serviceId,
  );

  if (appliedBundleApplicableForCheckout) return;

  toast(
    ({ closeToast }) => (
      <Snackbar
        label="Applied bundle not applicable for checkout" // <- @TDOO: translate
        type="highlight"
        action={<button onClick={closeToast}>OK</button>}
      />
    ),
    { autoClose: false },
  );

  dispatch(bookActions.removeBundle());
}

const BookingCheckout = ({ rendered }: { rendered: MutableRefObject<boolean> }) => {
  const {
    summary,
    selectedPaymentMethod,
    employee,
    formFields,
    cofThreeDS,
    submitting,
    swish,
    termsAccepted,
    missingActions,
    guestId,
    protectedEmailError,
    onlinePaymentRequiredEmail,
    groupBooking,
    triggeredTurnstile,
    session,
    bookingId,
    onToggleTerms,
    bookingCheckoutAPI,
    onCloseAdyenCheckoutModal,
    onResetPayment,
    onSubmitting,
    onChangePaymentMethod,
    onApplyGiftcard,
    onRemoveGiftcard,
    onRemoveBundle,
    onDismissSwishQRCode,
    onUpdateGroupBooking,
    onUseGuestCheckout,
    onValidateEmailProtected,
    onValidateOnlinePaymentRequiredEmail,
    onDismissEmailProtectedError,
    onCheckoutMissingAction,
  } = useBookingCheckout();
  const { storedPaymentMethods, manualSetPaymentMethods, paymentMethods, loading, isBokadirektPay, platformFee } =
    usePaymentMethodsContext();
  const klarnaBrands = getValidKlarnaBrands(
    paymentMethods,
    (summary?.finalPrice ?? 0) + (platformFee?.enabled ? platformFee.amount : 0),
  );

  const getExperimentVariant = useGetAmplitudeExperimentVariant();
  const { state } = useLocation<{ isSistaminuten?: boolean }>();
  const user = useAppSelector((state) => state.users.user);
  const hasFetchedUser = useAppSelector((state) => state.users.hasFetched);
  const isSistaMinutenGuestUser = state?.isSistaminuten && !user;
  const widgetLogin = useWidgetLogin();
  const widgetPayment = useWidgetPayment();
  const containment = getContainmentMode();
  // Framed (booking widget): login runs through the first-party popup, never in-frame.
  const framed = containment === 'embed';
  const resolvingSession = isResolvingContainedSession({
    mode: containment,
    hasToken: Boolean(loggedUser()),
    hasFetched: Boolean(hasFetchedUser),
  });
  // Contained, the widget supplies the login card checkout renders and the
  // panel the protected-e-mail modal renders, so checkout never imports the
  // feature. The panel is taken from the mode rather than from the card: the
  // login card holds itself back while a token resolves, but a blocked e-mail
  // must never be left without a way out.
  const InlineLogin = containment === 'linked' ? widgetLogin.LoginPanel : undefined;
  const ContainedLoginCard = widgetLogin.LoginCard;
  // The contained card's own login is what satisfies `requireLogin`, so without
  // this the card would take its success beat down with it in the commit that
  // starts it. The card reports itself busy from the code submission until the
  // beat has played, and is held mounted for as long as it says so.
  const [containedLoginBusy, setContainedLoginBusy] = useState(false);
  const history = useHistory();
  const location = useLocation<{ savedCheckoutState?: unknown }>();
  const screenName = getScreenNameFromPath(location.pathname);

  // experiment with radio buttons and popular badge
  const useHighlightedPaymentMethods = (() => {
    if (isSistaminuten()) return false;

    return getExperimentVariant('highlighted-payment-methods')?.value === 'variant-popular-badge-visible';
  })();

  const loginContext = useContext(LoginContext);
  const pinInputProps = usePinInput({
    fields: 6,
    validateCallback: loginContext.handleLogin,
  });
  const { onClose: onCheckoutModalClose, ...checkoutModalContext } = useCheckoutModal();
  const [klarnaVariant, setKlarnaVariant] = useState<KlarnaVariant | undefined>();
  const { formData, onUpdateFormData } = useCheckoutFormData();
  const checkoutFormContext = useCheckoutForm();
  const {
    formState: { dirtyFields, isValid },
    handleSubmit,
    setError: setFormError,
    setValue: setFormValue,
    watch,
    clearErrors: clearFormErrors,
  } = checkoutFormContext;
  const { isMobileView } = useMobileView();
  const { attribution } = useBookingFlowChrome();
  const [showEditProfile, setShowEditProfile] = useState(false);
  /**
   * if email is optional and there is no logged user, prompt user to fill in email
   * to get a better user experience
   */
  const [showPromptEmailField, setShowPromptEmailField] = useState(false);
  const updateUserInfo = useUpdateUserInfo({
    callback: (props) =>
      handleUpdateUserInfoCallback(
        { retry: props.retry, success: props.success, initiatedFrom: screenName },
        handleCloseEditProfile,
      ),
    onValidationError: () => {
      trackMpEvent({
        name: 'edit_profile_flow',
        properties: {
          step: 'error',
          initiated_from: screenName,
          error_type: 'validation_error',
        },
      });
    },
  });

  const appDispatch = useAppDispatch();

  const booking = useAppSelector((state) => state.book) as BookState;
  const turnstile = useAppSelector((state) => state.turnstile);

  // Refs
  const selectPaymentMethodContainerRef = useRef<HTMLDivElement>(null);
  const bookingTermsContainerRef = useRef<HTMLDivElement>(null);
  const confirmationContainerRef = useRef<HTMLDivElement>(null);
  const addParticipantContainerRef = useRef<HTMLDivElement>(null);
  const checkoutCTAPosition = useIntersectionObserver(confirmationContainerRef, { root: null, threshold: 0.3 });
  const isCheckoutCTAInView = checkoutCTAPosition === 'inView';
  const cardTracking = useCardPaymentTracking(
    { flow: 'booking', company: { id: booking.place.id, name: booking.place.about.name } },
    checkoutModalContext,
  );

  // Computed fields
  const isGroupBooking = booking.time.capacity > 1 && !booking.appliedBundle;
  const selectedCapacity = booking.time?.selectedCapacity || 1;
  const showSwishQrCode = Boolean(swish?.showQrCode);
  const showThreeDSecure = Boolean(cofThreeDS?.adyenActionJson);
  const hasFormfields = Boolean(formFields.length);
  const showFloatingCTA = isCheckoutCTAInView !== undefined && Boolean(!isCheckoutCTAInView);
  const isTermsAccepted = termsAccepted;
  const { shouldPromptGuestCheckout, isGuestCheckout, loggedOutCheckout } = getGuestCheckoutProps(
    booking.place,
    user,
    guestId,
  );

  // Captcha verification requirement for guests
  const requireCaptchaVerification = (() => {
    if (turnstile.verified || !isGuestCheckout) return false;

    const captchaVariant = getExperimentVariant('captcha_in_guest_checkout');
    return captchaVariant?.value !== 'captcha-off';
  })();

  // Captcha verification requirement for logged in users
  const requireCaptchaVerificationLoggedIn = (() => {
    if (!triggeredTurnstile || isGuestCheckout) return false;

    return true;
  })();

  const bookingTrackingProps = buildBookingTrackingProps({
    booking,
    employee,
    summary,
    selectedPaymentMethod,
    formFields,
    selectedCapacity,
  });

  const services = mapBookStateToCheckoutDetailsServices(booking, employee.shown, onRemoveBundle);
  const products = mapBookStateToCheckoutDetailsProducts(booking);

  const { giftcards, ...rest } = mapBookingCheckoutToCheckoutDetailsProps(
    summary,
    selectedPaymentMethod,
    employee.shown,
    booking,
  );

  const checkoutDetailsProps: CheckoutDetailsProps = {
    ...rest,
    services,
    products,
    giftcards: giftcards.map((giftcard) => ({
      ...giftcard,
      onRemove: onRemoveGiftcard(giftcard.code),
    })),
  };

  const isFullyPadWithGiftcard = isFullyPaidWithGiftcards(summary, Object.values(booking.usageReqId?.giftcards ?? {}));
  const showGlobalLoadingSpinner =
    submitting &&
    !(
      isBokadirektPay &&
      selectedPaymentMethod.type !== CHECKOUT_PAYMENT_METHOD.STORED_COF &&
      selectedPaymentMethod.type !== CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE
    );

  const checkoutTerms = getCheckoutTerms(
    'booking',
    getCheckoutTermsVariant(selectedPaymentMethod, isBokadirektPay),
    getBookingSubmitLabel({
      paymentMethod: selectedPaymentMethod.type,
      isMobileView,
      isFloating: !isCheckoutCTAInView,
      isFullyPadWithGiftcard,
    }),
    truncateString(checkoutDetailsProps.placeName, 45, 38),
  );

  // only move fields that are NOT dirty to update user info form (dirty fields are fields that have been changed by user)
  const dirty = dirtyFields && Object.keys(dirtyFields);
  const toUpdateUserInfoFormfields = formData[1]
    ? Object.fromEntries(
        Object.entries(formData[1]).filter(([key, value]) => !dirty.includes(key) && value !== undefined),
      )
    : {};

  const { email: _, ...prefilledUserProfileFormFields } = getUpdateEditProfileFields(toUpdateUserInfoFormfields);
  const updateUserInfoProps = {
    ...updateUserInfo,
    ...prefilledUserProfileFormFields,
    handleSubmit: () => {
      trackMpEvent({
        name: 'edit_profile_flow',
        properties: {
          step: 'verification',
          initiated_from: screenName,
          booking_session_id: bookingFlowTrackingSession.getId(),
        },
      });
      return updateUserInfo.handleSubmit();
    },
  };

  const requireLogin = (() => {
    if (isSistaminuten()) return false;

    if (!user && shouldRequireLogin(booking.place)) return true;

    if (isSistaMinutenGuestUser) return false;

    return shouldPromptGuestCheckout;
  })();

  // Handlers
  const handleApplyGiftcard = (code: string) => {
    onApplyGiftcard(code)((props) => {
      cardTracking.trackCardApplied(props);
      if (props.success) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        handleCloseCheckoutModal();
      }
    });
  };

  const handleOnAbortCheckoutModal = () => {
    cardTracking.trackCardEntryCancelled();
    handleCloseCheckoutModal();
  };

  const handleCloseCheckoutModal = () => {
    onCheckoutModalClose();
  };

  const handleCloseAdyenCheckoutModal = () => {
    if (checkoutModalContext.screen === 'bokadirektAddCard') {
      trackMpEvent({
        name: 'add_payment_card_flow',
        properties: { step: 'cancel', booking_session_id: bookingFlowTrackingSession.getId() },
      });
    }
    onCloseAdyenCheckoutModal();
    onCheckoutModalClose();
  };

  const handleAdyenAddCardError = () => {
    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'error', booking_session_id: bookingFlowTrackingSession.getId() },
    });
    onCloseAdyenCheckoutModal();
    onCheckoutModalClose();
  };

  const handleCloseEditProfile = () => {
    setShowEditProfile(false);

    if (!updateUserInfo.hasSavedOnce.current) {
      trackMpEvent({
        name: 'edit_profile_flow',
        properties: {
          step: 'cancel',
          initiated_from: screenName,
          booking_session_id: bookingFlowTrackingSession.getId(),
        },
      });
    }
  };

  const handleShowEditProfile = () => {
    setShowEditProfile(true);
    trackMpEvent({
      name: 'edit_profile_flow',
      properties: {
        step: 'initiated',
        initiated_from: screenName,
        booking_session_id: bookingFlowTrackingSession.getId(),
      },
    });
  };

  const handleOnCheckoutTermsOpen = () => {
    bookingFlowTrackingSession.terms('initiated', 'T&C_bd');
  };

  const handleOnPromptEmailFieldDismiss = () => {
    guestCheckoutManager().setOptions({ promptedEmail: true });
    setShowPromptEmailField(false);
    checkoutModalContext.onOpen();
  };

  const handleOnPromptEmailFieldContinue = () => {
    guestCheckoutManager().setOptions({ promptedEmail: true });
    setShowPromptEmailField(false);
    setFormError('email', { message: _s('EnterEmail'), type: 'required' }, { shouldFocus: true });
  };

  const handleChangePaymentMethod = (method: SelectedPaymentMethod) => {
    if (!loggedOutCheckout && method.type === CHECKOUT_PAYMENT_METHOD.NEW_COF && !isBokadirektPay) {
      checkoutModalContext.onOpen('cof');
      return;
    }

    if (!loggedOutCheckout && method.type === CHECKOUT_PAYMENT_METHOD.NEW_COF && isBokadirektPay) {
      checkoutModalContext.onOpen('bokadirektAddCard');
      onChangePaymentMethod(method);
      return;
    }

    onChangePaymentMethod(method);
    cardTracking.trackPaymentMethodChange(selectedPaymentMethod, method);

    const buildSummaryProperties = getSummaryProperties({
      summary,
      selectedPaymentMethod: method,
      employee,
      isBokadirektPay,
      hasPlatformFee: isBokadirektPay && (platformFee?.amount ?? 0) > 0,
    });
    bookingFlowTrackingSession.setProperties(
      buildSummaryProperties({ type: 'booking_flow', data: { ...booking, formFields } }),
    );

    if (loggedOutCheckout && method.type === CHECKOUT_PAYMENT_METHOD.NEW_COF) {
      return;
    }

    onCheckoutModalClose();
  };

  const handleCloseLoginModal = () => {
    loginTrackingSession.track({ step: 'back_to_email_submission', flow: loginContext.flow });
    loginContext.clearLoginState();
  };

  const handleOnCardAdded = (card: PaymentCard) => {
    cardTracking.trackPaymentCardAdded(card);
    handleChangePaymentMethod({
      type: CHECKOUT_PAYMENT_METHOD.STORED_COF,
      brand: card.brand,
      id: card.id,
      lastFour: card.lastFour,
    });
  };

  const handleCloseAdditionalParticipantModal = () => {
    onUpdateGroupBooking({
      showModal: false,
      isUpdate: false,
      participantId: groupBooking.hasAddedParticipant ? null : groupBooking.participantId,
    });
  };

  const handleDismissSwishQRCode = () => {
    onDismissSwishQRCode();
  };

  const handleSubmitAdditionalParticipantModal = () => {
    onUpdateGroupBooking({ showModal: false, isUpdate: false, participantId: null });
  };

  /**
   * Get a participant to show in AdditionalParticipant modal
   */
  const getCurrentParticipant = (): {
    participantFormId: string; // Id that participant will have in the form that is sent in booking request
    initialUser?: Record<string, string>;
  } => {
    // Prefill if updating an existing participant or adding the first participant to the form
    const initialUser = groupBooking.isUpdate ? formData[groupBooking.participantId] : undefined;

    const participantFormId = (() => {
      if (groupBooking.isUpdate) {
        return groupBooking.participantId;
      }

      return (Object.keys(formData).length + 1).toString();
    })();

    return {
      initialUser,
      participantFormId,
    };
  };

  /**
   * Get added participants that is going to be sent in booking request.
   * In case user have not added at least one participant then none is returned.
   */
  const getAddedParticipants = () => {
    const participants = groupBooking.hasAddedParticipant
      ? Object.entries(formData).map(([key, value]) => ({
          id: key,
          ...(value.givenName || value.familyName ? { name: `${value.givenName} ${value.familyName}` } : {}),
          ...(value.email ? { email: value.email } : {}),
        }))
      : [];

    /**
     * Dont show the first participant (main booker) in the AdditionalParticipants section
     */
    return participants.filter((participant) => participant.id !== '1');
  };

  const handleOpenAdditionalParticipantModal = (participantId?: string) => {
    const isUpdatingExistingParticipant = Boolean(participantId);

    onUpdateGroupBooking({
      ...(isUpdatingExistingParticipant ? { isUpdate: true, participantId: participantId } : {}),
      showModal: true,
    });
  };

  const handleOnDismissEmailProtectedError = () => {
    setFormValue('email', undefined);
    clearFormErrors('email');
    onDismissEmailProtectedError();
    loginContext.clearLoginState();
  };

  // Logging in answers the protection, so the modal has nothing left to ask.
  // The address stays where dismissing clears it: it is the account they just
  // proved is theirs, and retyping it is the last thing they should have to do.
  const handleProtectedEmailLoggedIn = () => {
    clearFormErrors('email');
    onDismissEmailProtectedError();
  };

  const handleBeforeThreeDSRedirect = () => {
    saveBookingCheckoutStateToLocalStorage({
      formData,
      formFields,
      missingActions,
      selectedPaymentMethod,
      submitting,
      summary,
      termsAccepted,
      cofThreeDS,
    });
  };

  const handleOnCompleteAdyenCheckout = useOnCompleteAdyenCheckout({
    session,
    resourceId: bookingId,
    storedPaymentMethods,
    paymentMethods,
    manualSetPaymentMethods,
    onSubmitting,
    onCardAdded: handleOnCardAdded,
    onNoNewCardFound: handleCloseAdyenCheckoutModal,
    onError: () => {
      handleCloseAdyenCheckoutModal();
      toast(({ closeToast }) => (
        <Snackbar label={_s(`${baseTranslationKey}.adyenCheckoutError`)} type="danger" onClose={closeToast} />
      ));
    },
    trackingSessionId: bookingFlowTrackingSession.getId(),
  });

  const handleContinueAsGuest = () => {
    onUseGuestCheckout();
  };

  function handlePageLeave() {
    if (requireLogin && loginTrackingSession.isActive()) {
      loginContext.onClose();
    }

    /**
     * if listview place the page leave will navigate back to place page
     * and not timepicker, so booking-flow is canclled
     */
    if (booking.place?.about?.settings?.listView) {
      bookingFlowTrackingSession.cancelSession();
    } else {
      bookingFlowTrackingSession.backToTimepicker();
    }
  }

  // Effects
  useEffect(() => {
    if (!checkoutModalContext.isOpen) return;

    const { screen } = checkoutModalContext;

    if (screen === 'bokadirektAddCard') {
      trackMpEvent({
        name: 'add_payment_card_flow',
        properties: { step: 'initiated', booking_session_id: bookingFlowTrackingSession.getId() },
      });
    }
  }, [checkoutModalContext.isOpen, checkoutModalContext.screen]);

  useEffect(() => {
    rendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Embedded widget only: every Bokadirekt Pay method that mounts an Adyen session pays in
  // the popup the frame opens (a stored card authorised without 3DS never leaves the frame:
  // it reconciles on the validate route, which has its own `<Redirect>`). The provider
  // reports the popup's outcome through its state; this reacts to it.
  useWidgetPaymentReactions({ onCheckoutModalClose, onResetPayment });

  useEffect(() => {
    if (bookingTrackingProps?.transactionProps?.allowMarketing) {
      trackPage();

      const cioData: any = {
        merchant_id: bookingTrackingProps?.transactionProps?.place?.id,
        merchant_name: bookingTrackingProps?.transactionProps?.place?.name,
        service_name: bookingTrackingProps?.transactionProps?.contentName,
      };
      if (bookingTrackingProps?.transactionProps?.isSistaminutentiderBooking) {
        cioData.is_sistaminuten = true;
      }
      trackPageCio('booking_step_confirm_booking', cioData);
    }
    salonTracking(booking.place);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (missingActions.includes('payment-method') && isValid) {
      selectPaymentMethodContainerRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
    } else if (missingActions.includes('practitioner-terms') && isValid) {
      bookingTermsContainerRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
    } else if (missingActions.includes('participants')) {
      addParticipantContainerRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingActions]);

  useEffect(() => {
    if (user && loginContext.view === 'verifyOtp') {
      loginContext.updateView('verifySuccess');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loginContext.view]);

  // Framed, the login runs in the popup and hands back to WidgetLogin, so no
  // callback of ours reports it. The user arriving in the store is the only
  // signal this modal gets that its question has been answered.
  useEffect(() => {
    if (framed && protectedEmailError && user) handleProtectedEmailLoggedIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framed, protectedEmailError, user]);

  /**
   * If user is not logged in we need to check if email is protected
   * and throw a modal to force user to sign in to continue with email
   */
  useEffect(() => {
    if (!isGuestCheckout) return;

    const subscription = watch((field, info) => {
      const value = info.name === 'email' && info.type === 'change' && field?.['email'];

      if (!value || value === undefined) return;

      const isValidEmail = isEmail(value.trim(), { allow_utf8_local_part: false });

      if (isValidEmail === true) {
        onValidateEmailProtected(value, () => setFormError('email', { message: _s('EmailProtected') }));
        onValidateOnlinePaymentRequiredEmail(booking.place.id, value);
      } else if (isValidEmail === false && onlinePaymentRequiredEmail) {
        onValidateOnlinePaymentRequiredEmail(booking.place.id, value);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, isGuestCheckout]);

  /**
   * listen to history changes to detect when user navigates back
   */
  useEffect(() => listenForBackNavigation(history, handlePageLeave), [history]);

  return (
    <CheckoutViewLayout
      header={{
        leftSlot: {
          button: {
            item: {
              identifier: HeaderButtonIdentifier.Back,
              icon: 'back',
            },
            'aria-label': _t('chooseTime'),
            onClick: () => history.go(-1),
          },
          text: _t('chooseTime'),
        },
        middleSlot: {
          title: !user && !isGuestCheckout ? _t('logIn') : _t('confirmBooking'),
          subtitle: checkoutDetailsProps.placeName,
        },
        rightSlot:
          (!isMobileView || requireCaptchaVerification || requireCaptchaVerificationLoggedIn) &&
          checkoutDetailsProps.logo
            ? { imgUrl: checkoutDetailsProps.logo }
            : undefined,
      }}
      wrapperClass="bg-surface_mp lg:bg-gradient-surface-mp min-h-viewport">
      {showGlobalLoadingSpinner && <LoadingPlaceHolder className="fixed h-screen" />}
      {requireCaptchaVerification && !requireCaptchaVerificationLoggedIn && (
        <div className="pt-xl flex items-center justify-center bg-white">
          <Turnstile
            action="guest-booking-checkout"
            sitekey={config.turnstileSiteKey}
            onVerify={async (token: string) => {
              const action = await turnstileActions.verifyToken(token);
              appDispatch(action);
              return;
            }}
            onError={(error) => {
              Sentry.captureMessage(`Turnstile onError callback called with error-code=${error}`);
              appDispatch(turnstileActions.removeToken());
            }}
            onExpire={(_, bound) => {
              appDispatch(turnstileActions.removeToken());
              bound.execute();
            }}
            onUnsupported={() => Sentry.captureMessage('Turnstile onUnsupported callback called')}
            theme="light"
            appearance="execute"
            execution="render"
            language={getLang()}
          />
        </div>
      )}
      {requireCaptchaVerificationLoggedIn && !requireCaptchaVerification && (
        <div className="pt-xl flex items-center justify-center bg-white">
          <Turnstile
            action="booking-checkout"
            sitekey={config.turnstileSiteKey}
            onVerify={async (token: string) => {
              bookingCheckoutAPI().submitPayAtPlace(token);
            }}
            onError={(error) => {
              Sentry.captureMessage(`Turnstile onError callback called with error-code=${error}`);
            }}
            onExpire={(_, bound) => {
              bound.execute();
            }}
            onUnsupported={() => Sentry.captureMessage('Turnstile onUnsupported callback called')}
            theme="light"
            appearance="execute"
            execution="render"
            language={getLang()}
          />
        </div>
      )}
      {!requireCaptchaVerification && !requireCaptchaVerificationLoggedIn && (
        <div className="bg-surface_mp lg:bg-transparent">
          <div className="lg:py-xxl lg:container">
            <div className="gap-xxl flex items-start">
              <div className="gap-lg flex grow-[9999] basis-[600px] flex-col">
                <CardWrapper className="w-full lg:hidden">
                  <CheckoutDetails {...checkoutDetailsProps} />
                </CardWrapper>
                {requireLogin && resolvingSession && (
                  <CardWrapper
                    className={containment ? 'lg:border-border_regular lg:!border lg:!shadow-none' : undefined}>
                    <div className="py-xxl flex justify-center">
                      <LoadingIcon className="h-8 w-8" />
                    </div>
                  </CardWrapper>
                )}
                {(requireLogin || containedLoginBusy) &&
                  !resolvingSession &&
                  (ContainedLoginCard ? (
                    <ContainedLoginCard
                      prefillEmail={formData['1']?.email}
                      requireAccount={Boolean(shouldRequireLogin(booking.place))}
                      onBusyChange={setContainedLoginBusy}
                      {...(shouldPromptGuestCheckout && { continueAsGuest: handleContinueAsGuest })}
                    />
                  ) : (
                    <CardWrapper>
                      <UserLogin
                        {...loginContext}
                        initiatedFrom="checkout_prior_booking_summary"
                        {...(shouldPromptGuestCheckout && { continueAsGuest: handleContinueAsGuest })}
                      />
                    </CardWrapper>
                  ))}
                {!requireLogin && (
                  <>
                    {user && (
                      <>
                        <CardWrapper>
                          <UserInfo
                            email={user?.contact?.email ?? ''}
                            name={`${user?.about?.givenName ?? ''} ${user?.about?.familyName ?? ''}`}
                            phone={user?.contact?.phone ?? ''}
                            initials={getUserInitials(user)}
                            onClick={handleShowEditProfile}
                          />
                        </CardWrapper>
                        {isGroupBooking && hasFormfields && (
                          <CheckoutFormFields
                            context={checkoutFormContext}
                            isGuestCheckout={isGuestCheckout}
                            formFields={formFields}
                            initialFormValues={formData['1']}
                            onUpdateFormField={onUpdateFormData}
                            useCardWrapper={true}
                          />
                        )}
                      </>
                    )}
                    {(!isGroupBooking || !user) && hasFormfields && (
                      <CheckoutFormFields
                        context={checkoutFormContext}
                        isGuestCheckout={isGuestCheckout}
                        formFields={formFields}
                        initialFormValues={formData['1']}
                        onUpdateFormField={onUpdateFormData}
                        useCardWrapper={true}
                      />
                    )}
                    {isGroupBooking && (
                      <CardWrapper>
                        <div ref={addParticipantContainerRef}>
                          <AdditionalParticipants
                            capacity={booking.time.capacity}
                            onUpdateParticipants={(id) => handleOpenAdditionalParticipantModal(id)}
                            onAddParticipants={() => handleOpenAdditionalParticipantModal()}
                            participants={getAddedParticipants()}
                          />
                        </div>
                      </CardWrapper>
                    )}
                    {employee.terms && (
                      <CardWrapper
                        className={classnames(
                          missingActions.includes('practitioner-terms') && 'border-border_negative !border-[1px]',
                        )}>
                        <div ref={bookingTermsContainerRef}>
                          <BookingTerms
                            terms={employee.terms}
                            termsAccepted={termsAccepted}
                            error={missingActions.includes('practitioner-terms')}
                            onChange={(checked) => onToggleTerms(checked)}
                          />
                        </div>
                      </CardWrapper>
                    )}
                    {summary.canPayOnline && (
                      <CardWrapper
                        className={classnames(
                          missingActions.includes('payment-method') && 'border-border_negative !border-[1px]',
                        )}>
                        <div ref={selectPaymentMethodContainerRef}>
                          <PromotedPaymentMethods
                            summary={summary}
                            loading={isBokadirektPay && loading}
                            selectedPaymentMethod={selectedPaymentMethod}
                            onChangePaymentMethod={handleChangePaymentMethod}
                            triggerSource="checkout_booking_summary"
                            useHighlighted={useHighlightedPaymentMethods}
                            error={missingActions.includes('payment-method')}
                            isOnlinePaymentRequiredCustomer={onlinePaymentRequiredEmail}
                            {...checkoutModalContext}
                          />
                        </div>
                      </CardWrapper>
                    )}
                    <div ref={confirmationContainerRef}>
                      <CardWrapper>
                        <div className="gap-xl p-lg flex flex-col">
                          <SubmitCTA data-cy="bookingCheckoutSubmitButton" setKlarnaVariant={setKlarnaVariant} />
                          <CheckoutTerms
                            description={checkoutTerms.description}
                            linkLabel={checkoutTerms.linkLabel}
                            termsUrl={checkoutTerms.termsUrl}
                            onTermsOpen={handleOnCheckoutTermsOpen}
                          />
                        </div>
                      </CardWrapper>
                    </div>
                    <FloatingCTAWrapper
                      description={checkoutTerms.description}
                      linkLabel={checkoutTerms.linkLabel}
                      termsUrl={checkoutTerms.termsUrl}
                      show={showFloatingCTA && isMobileView}
                      onTermsOpen={handleOnCheckoutTermsOpen}>
                      <SubmitCTA floating setKlarnaVariant={setKlarnaVariant} />
                    </FloatingCTAWrapper>
                  </>
                )}
                {/* Below `lg` the summary column is hidden, so the page's last row carries it instead. */}
                {attribution ? <div className="lg:hidden">{attribution('column')}</div> : null}
              </div>
              <div className="lg:gap-lg sticky top-20 hidden lg:flex lg:grow-[100] lg:basis-[390px] lg:flex-col">
                <CardWrapper className="w-full">
                  <CheckoutDetails key={2} {...checkoutDetailsProps} />
                  {showFloatingCTA && !isMobileView && (
                    <div
                      className={classnames(
                        'gap-xl p-lg flex flex-col',
                        !checkoutDetailsProps.information &&
                          !checkoutDetailsProps.paymentMethod &&
                          'border-black-100 border-t',
                      )}>
                      <SubmitCTA setKlarnaVariant={setKlarnaVariant} />
                      <CheckoutTerms
                        description={checkoutTerms.description}
                        linkLabel={checkoutTerms.linkLabel}
                        termsUrl={checkoutTerms.termsUrl}
                        onTermsOpen={handleOnCheckoutTermsOpen}
                      />
                    </div>
                  )}
                </CardWrapper>
                {attribution?.('column')}
              </div>
            </div>
          </div>
        </div>
      )}
      {checkoutModalContext.screen === 'klarnaOptions' && (
        <KlarnaOptionsModal
          isOpen={checkoutModalContext.isOpen}
          onClose={onCheckoutModalClose}
          brands={klarnaBrands}
          onSelect={async (variant) => {
            setKlarnaVariant(variant);
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await bookingCheckoutAPI().submitAdyen();
            if (!success) onCheckoutModalClose();
          }}
        />
      )}
      {checkoutModalContext.screen === 'walleyPhoneInput' && (
        <WalleyPhoneModal
          isOpen={checkoutModalContext.isOpen}
          onClose={onCheckoutModalClose}
          onSubmit={async (phone) => {
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await bookingCheckoutAPI().submitAdyen({ walleyPhone: phone });
            if (!success) onCheckoutModalClose();
          }}
        />
      )}
      {(checkoutModalContext.screen === 'bokadirektPay' || checkoutModalContext.screen === 'bokadirektAddCard') &&
        (showsWidgetPaymentModal({ embedded: isEmbedMode(), state: widgetPayment.state }) ? (
          // The payment runs in the frame's popup; the Adyen component's session would be stale.
          <WidgetPaymentModal isOpen={checkoutModalContext.isOpen} paymentMethodType={selectedPaymentMethod.type} />
        ) : (
          <AdyenCheckoutComponent
            session={session}
            onClose={handleCloseAdyenCheckoutModal}
            onError={checkoutModalContext.screen === 'bokadirektAddCard' ? handleAdyenAddCardError : undefined}
            isOpen={checkoutModalContext.isOpen}
            submitting={submitting}
            onComplete={handleOnCompleteAdyenCheckout}
            amount={selectedPaymentMethod.final}
            googlePayLineItems={
              selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY
                ? [
                    ...getGooglePaySummaryServiceItems(mapBookStateToCheckoutDetailsServices(booking, employee.shown)),
                    ...getGooglePaySummaryProductItems(mapBookStateToCheckoutDetailsProducts(booking)),
                    ...getGooglePaySummaryGiftcardItems(booking),
                    ...getGooglePaySummaryFeeItems(selectedPaymentMethod),
                  ]
                : undefined
            }
            paymentMethodType={
              checkoutModalContext.screen === 'bokadirektAddCard'
                ? CHECKOUT_PAYMENT_METHOD.NEW_COF
                : selectedPaymentMethod.type
            }
            klarnaVariant={checkoutModalContext.screen === 'bokadirektPay' ? klarnaVariant : undefined}
          />
        ))}
      {checkoutModalContext.screen === 'cof' && (
        <CreditCardModal
          isOpen={checkoutModalContext.isOpen}
          onClose={handleCloseCheckoutModal}
          onBeforeThreeDSRedirect={handleBeforeThreeDSRedirect}
          onCardAdded={handleOnCardAdded}
          returnPath="/booking/checkout/validate-3ds-redirect"
          {...(loggedOutCheckout && {
            guestId,
            onAuthorize: (data) => {
              bookingCheckoutAPI().submitGuestCoF(data);
              onCheckoutModalClose();
            },
            amount: selectedPaymentMethod.final,
          })}
          {...checkoutModalContext}
          traceTrackingSession={{ booking_session_id: bookingFlowTrackingSession.getId() }}
        />
      )}
      {checkoutModalContext.screen !== 'cof' &&
        checkoutModalContext.screen !== 'bokadirektPay' &&
        checkoutModalContext.screen !== 'bokadirektAddCard' &&
        checkoutModalContext.screen !== 'klarnaOptions' &&
        checkoutModalContext.screen !== 'walleyPhoneInput' && (
          <CheckoutModal
            selectedPaymentMethod={selectedPaymentMethod}
            onChangePaymentMethod={handleChangePaymentMethod}
            onApplyGiftcard={handleApplyGiftcard}
            onClose={handleOnAbortCheckoutModal}
            {...checkoutModalContext}
          />
        )}
      {showThreeDSecure && (
        <Modal isOpen={!submitting}>
          <Modal.Content floating={!isMobileView}>
            <div className={classnames('gap-xl pb-lg px-lg flex flex-col', isMobileView && 'px-lg')}>
              <ThreeDSComponent
                action={JSON.parse(cofThreeDS?.adyenActionJson)}
                handleOnAdditionalDetails={(data) => bookingCheckoutAPI().submitThreeDS(data)}
              />
            </div>
          </Modal.Content>
        </Modal>
      )}
      {showSwishQrCode && <SwishModal onClose={handleDismissSwishQRCode} qrCode={swish.payment.qrCode} />}
      {loginContext.view === 'verifyOtp' && (
        <Modal isOpen>
          <Modal.Content floating={!isMobileView}>
            <Modal.Header
              title={_s(`loginModal.verifyOtp.title`)}
              {...(loginContext.view === 'verifyOtp' && { onClose: handleCloseLoginModal })}
            />
            <div className={classnames('gap-xl pb-lg px-lg flex flex-col', isMobileView && 'px-lg')}>
              {loginContext.view === 'verifyOtp' && <VerifyOTP {...loginContext} {...pinInputProps} />}
            </div>
          </Modal.Content>
        </Modal>
      )}
      {protectedEmailError && loginContext.view === 'requestToken' && (
        <Modal isOpen>
          <Modal.Content floating={!isMobileView} gradientBackground>
            <Modal.Header
              title={_s(`${baseTranslationKey}.protectedUserLoginModal.title`)}
              onClose={handleOnDismissEmailProtectedError}
            />
            <div className={classnames(isMobileView && 'px-lg')}>
              <ProtectedUserLogin
                {...loginContext}
                email={{ ...loginContext.email, value: formData['1']?.email }}
                {...(framed && { onLoginPopup: () => widgetLogin.openLoginPopup({ email: formData['1']?.email }) })}
                {...(InlineLogin && {
                  inlineLogin: (
                    <InlineLogin prefillEmail={formData['1']?.email} onLoggedIn={handleProtectedEmailLoggedIn} />
                  ),
                })}
              />
            </div>
          </Modal.Content>
        </Modal>
      )}
      <Modal isOpen={showPromptEmailField}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header title={_s(`${baseTranslationKey}.promptEmailFieldModal.title`)} />
          <div className={classnames('space-y-lg flex flex-col', isMobileView && 'px-lg')}>
            <div className="space-y-md flex flex-col">
              <p>{_s(`${baseTranslationKey}.promptEmailFieldModal.body.p1`)}</p>
              <p>{_s(`${baseTranslationKey}.promptEmailFieldModal.body.p2`)}</p>
            </div>
            <div className="space-x-md flex justify-end">
              <Button variant="link" onClick={handleOnPromptEmailFieldDismiss}>
                {_s(`${baseTranslationKey}.promptEmailFieldModal.cta.secondary`)}
              </Button>
              <Button variant="primary" onClick={handleOnPromptEmailFieldContinue}>
                {_s(`${baseTranslationKey}.promptEmailFieldModal.cta.primary`)}
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>
      <Modal isOpen={showEditProfile} onRequestClose={handleCloseEditProfile}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header title={_s(`${baseTranslationKey}.editProfileModal.title`)} onClose={handleCloseEditProfile} />
          <div className={classnames(isMobileView && 'px-lg')}>
            <UpdateUserInfo {...updateUserInfoProps} />
          </div>
        </Modal.Content>
      </Modal>
      {groupBooking.showModal && (
        <AdditionalParticipantsModal
          capacity={booking.time.capacity}
          updateParticipant={groupBooking.isUpdate}
          formFields={formFields}
          onClose={handleCloseAdditionalParticipantModal}
          onSubmit={handleSubmitAdditionalParticipantModal}
          {...getCurrentParticipant()}
        />
      )}
      <SEO
        {...seoPropsFromBaseString('checkout', `${url.getBaseUrl()}booking/checkout`)}
        noindex={isSistaminuten()}
        image={url.getBaseImageUrl()}
      />
    </CheckoutViewLayout>
  );
};

const SubmitCTA = ({
  floating = false,
  setKlarnaVariant,
  ...props
}: { floating?: boolean; setKlarnaVariant: (variant: KlarnaVariant) => void } & ButtonProps) => {
  const {
    isBokadirektPay,
    paymentMethods: submitPaymentMethods,
    platformFee: submitPlatformFee,
  } = usePaymentMethodsContext();
  const {
    selectedPaymentMethod,
    summary,
    submitting,
    termsAccepted,
    guestId,
    bookingCheckoutAPI,
    onCheckoutMissingAction,
    onTriggeredTurnstile,
    formFields,
  } = useBookingCheckout();
  const klarnaBrands = getValidKlarnaBrands(
    submitPaymentMethods,
    (summary?.finalPrice ?? 0) + (submitPlatformFee?.enabled ? submitPlatformFee.amount : 0),
  );
  const { handleSubmit } = useCheckoutForm();
  const { formData } = useCheckoutFormData();
  const { isMobileView } = useMobileView();
  const checkoutModalContext = useCheckoutModal();
  const getExperimentVariant = useGetAmplitudeExperimentVariant();
  const booking = useAppSelector((state) => state.book) as BookState;
  const user = useAppSelector((state) => state.users)?.user;
  const { verified } = useAppSelector((state) => state.turnstile);
  const paymentMethod = selectedPaymentMethod.type;
  const widgetPayment = useWidgetPayment();
  const { final } = summary.availablePaymentMethods.find((method) => method.type === paymentMethod);
  const isTermsAccepted = termsAccepted;
  const isValidPaymentMethod = paymentMethod !== CHECKOUT_PAYMENT_METHOD.NONE;
  const { loggedOutCheckout, isGuestCheckout } = getGuestCheckoutProps(booking.place, user, guestId);
  const appliedGiftcards = Object.values(booking.usageReqId?.giftcards ?? {});
  const isFullyPadWithGiftcard = isFullyPaidWithGiftcards(summary, appliedGiftcards);

  const requireCaptchaVerification = (() => {
    if (verified || !isGuestCheckout) return false;

    const captchaVariant = getExperimentVariant('captcha_in_guest_checkout');

    return captchaVariant?.value !== 'captcha-off';
  })();

  const submitBooking = async (missingActions: CheckoutMissingAction[]) => {
    if (missingActions.length) {
      return;
    }

    if (isBokadirektPay && paymentMethod !== CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE) {
      if (paymentMethod === CHECKOUT_PAYMENT_METHOD.KLARNA) {
        if (klarnaBrands.length === 1) {
          setKlarnaVariant(klarnaBrands[0]);
          checkoutModalContext.onOpen('bokadirektPay');
          const success = await bookingCheckoutAPI().submitAdyen();
          if (!success) checkoutModalContext.onClose();
          return;
        }
        return checkoutModalContext.onOpen('klarnaOptions');
      }
      if (paymentMethod === CHECKOUT_PAYMENT_METHOD.WALLEY) {
        const userPhone = user?.contact?.mobile || user?.contact?.phone;
        const hasValidUserPhone = userPhone ? validatePhoneNumber(userPhone) : false;
        const formPhoneField = formFields.find((f) => (f.id === 'mobile' || f.id === 'phone') && f.visible);
        const formPhone = formPhoneField ? formData['1']?.[formPhoneField.id as string] || '' : '';
        if (!hasValidUserPhone && !formPhone) {
          return checkoutModalContext.onOpen('walleyPhoneInput');
        }
        checkoutModalContext.onOpen('bokadirektPay');
        const phoneToSend = formPhone || (hasValidUserPhone ? userPhone : undefined);
        const success = await bookingCheckoutAPI().submitAdyen({ walleyPhone: phoneToSend });
        if (!success) checkoutModalContext.onClose();
        return;
      }
      if (paymentMethod !== CHECKOUT_PAYMENT_METHOD.STORED_COF) {
        checkoutModalContext.onOpen('bokadirektPay');
      }
      const success = await bookingCheckoutAPI().submitAdyen();
      if (!success) checkoutModalContext.onClose();
      return;
    }

    switch (paymentMethod) {
      case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE:
        if (verified || isGuestCheckout) {
          return bookingCheckoutAPI().submitPayAtPlace();
        }
        return onTriggeredTurnstile();
      case CHECKOUT_PAYMENT_METHOD.STORED_COF:
        return bookingCheckoutAPI().submitCoF(selectedPaymentMethod.id);
      case CHECKOUT_PAYMENT_METHOD.SWISH:
        return bookingCheckoutAPI().submitSwish(booking);
      case CHECKOUT_PAYMENT_METHOD.QLIRO:
        return bookingCheckoutAPI().initQliro();
      case CHECKOUT_PAYMENT_METHOD.KLARNA:
        return bookingCheckoutAPI().initKlarna();
      case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
        return bookingCheckoutAPI().submitGooglePay(final);
      case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
        return bookingCheckoutAPI().submitApplePay(final);
      case CHECKOUT_PAYMENT_METHOD.NEW_COF:
        if (loggedOutCheckout) {
          return checkoutModalContext.onOpen('cof');
        }
        break;
    }
  };

  const ctaIconVariant = getCheckoutSubmitIconVariantIfAny(
    selectedPaymentMethod,
    isFullyPadWithGiftcard,
    appliedGiftcards,
  );

  const buttonProps: ButtonProps = {
    size: floating ? 'sm' : 'lg',
    block: !floating,
    // Framed: no second submit while a popup is live, nor before a pre-opened submit has settled.
    disabled: submitting || requireCaptchaVerification || isWidgetPaymentBlockingSubmit(widgetPayment),
    label: getBookingSubmitLabel({
      paymentMethod,
      isFloating: floating,
      isMobileView: isMobileView,
      isFullyPadWithGiftcard,
    }),
    onClick: () => {
      bookingFlowTrackingSession.bookingCTAClicked();

      onSubmitBookingValidation(
        {
          isTermsAccepted,
          isValidPaymentMethod,
          onCheckoutMissingAction,
        },
        handleSubmit,
        submitBooking,
      );
    },
    className: 'whitespace-nowrap',
    leftIcon: ctaIconVariant ? <Icon variant={ctaIconVariant} noFilter /> : null,
    iconNoFilter: Boolean(ctaIconVariant),
  };

  return <Button {...buttonProps} variant="primary" {...props} />;
};

function withValidatedBookState<P>(WrappedComponent: React.ComponentType<P>) {
  const WithValidatedBookState = (props: P) => {
    const history = useHistory();
    const bookState = useAppSelector((state) => state.book);
    const parsedBookState = bookStateSchema.safeParse(bookState);

    if (parsedBookState.success === false) {
      if (history.location.state && sessionStorage.getItem('bookPlace')) {
        Sentry.captureException(parsedBookState.error);
      }

      bookingFlowTrackingSession.error(['summary_error']);

      return <GoBack />;
    }

    return <WrappedComponent {...props} />;
  };
  return WithValidatedBookState;
}

/**
 * Wrap the checkout page in a component that checks if the book state is valid
 * and other validation that needs to be done before rendering the checkout page.
 */
export default withPlaceDetailsFallback(
  withValidatedBookState(() => {
    const dispatch = useDispatch();
    const location = useLocation<{ savedCheckoutState?: unknown }>();
    const history = useHistory();
    const user = useAppSelector((state) => state?.users?.user);
    // The cards endpoint authenticates off the session token, which outlives a late or failed profile fetch.
    // The profile selector stays so a mid-checkout login re-renders this.
    const isLoggedIn = !!user || !!loggedUser();
    const bookState = useAppSelector((state) => state.book);

    /**
     * If user is logged out logs in inside checkout the component will be re-rendered
     * To avoid running the on mount effects twice we use a ref to check if the checkout
     * component has been rendered before.
     */
    const childRendered = useRef(false);

    const { saved, services, time, place } = bookState as BookState;
    const invalidState = saved || !services || !services.length || !place || !time || !time.timestamp;

    if (invalidState) {
      bookingFlowTrackingSession.error(['summary_error']);
      return <GoBack />;
    }

    const employee = getBookingCheckoutEmployee(location.state, bookState);
    const { legacyPaymentsUnavailable } = getContainmentPolicy();
    const summary = getBookingCheckoutSummary({
      booking: bookState,
      employee,
      isOnlinePaymentRequiredCustomer: place.isOnlinePaymentRequiredCustomer,
      legacyPaymentsUnavailable,
    });

    const isBokadirektPay = isBokadirektPayCheckout(employee.actual);

    const checkoutId = getCheckoutID(bookState, employee.actual);

    const hasCoF = hasPaymentMethod(summary, CHECKOUT_PAYMENT_METHOD.STORED_COF);
    const { cards, loading, error } = useCardsManager([], isLoggedIn && hasCoF);
    const isLoadingCards = loading && !error && !cards.length;
    const { paymentHistory, loading: loadingPaymentHistory } = usePaymentsHistory(place.id);
    const validSavedCheckoutState = bookingCheckoutStateSchema.safeParse(location.state?.savedCheckoutState);
    const initialFormData = validSavedCheckoutState.success ? validSavedCheckoutState.data.formData : undefined;

    useExternalScript({ src: 'https://pay.google.com/gp/p/js/pay.js', cleanup: false, load: hasCoF });

    useEffect(() => {
      dispatch(bookActions.addUuid(checkoutId));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutId]);

    useEffect(() => {
      validateAppliedBookingBundleIfAny(bookState, dispatch);
      const paymentRedirectResultSuccess = getPaymentRedirectResultIfAny(location, history);
      if (paymentRedirectResultSuccess || paymentRedirectResultSuccess === undefined) return;
      toast(({ closeToast }) => (
        <Snackbar label={_s(`${baseTranslationKey}.paymentRedirectError`)} type="danger" onClose={closeToast} />
      ));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoadingCards || loadingPaymentHistory) return <LoadingPlaceHolder />;

    return (
      <UsePaymentMethodsProvider
        employeeId={employee.actual.id}
        placeId={place.id}
        fetchOnMount={isBokadirektPay}
        isBokadirektPay={isBokadirektPay}>
        <WithPaymentMethodsLoaded>
          <UseCardsProvider
            initialCards={cards}
            fetchOnMount={!isBokadirektPay && isLoggedIn && !legacyPaymentsUnavailable}>
            <CheckoutFormDataProvider employeeId={String(employee.actual.id)} initialFormData={initialFormData}>
              <BookingCheckoutProvider paymentHistory={paymentHistory}>
                <CheckoutModalProvider>
                  <CheckoutFormProvider>
                    <BookingCheckout rendered={childRendered} />
                  </CheckoutFormProvider>
                </CheckoutModalProvider>
              </BookingCheckoutProvider>
            </CheckoutFormDataProvider>
          </UseCardsProvider>
        </WithPaymentMethodsLoaded>
      </UsePaymentMethodsProvider>
    );
  }),
);
